/**
 * #574 — Texto ÚNICO de la política de cancelación, alineado a lo que el sistema
 * HACE: con prepago, las penalizaciones (cancelación tardía e inasistencia) se
 * RETIENEN del pago y se REEMBOLSA el resto; nunca se cobra una tarjeta nueva.
 *
 * Vive en un solo lugar para que el texto del cliente (PublicBookingPage) y el
 * del editor del tenant (BookingViews) no vuelvan a divergir — justo esa
 * duplicación dejó vivo el "guardamos tu método para ese cargo" que prometía un
 * cobro que ya no ocurre.
 *
 * Con `price` (pesos) arma los montos entre paréntesis; sin él, solo
 * porcentajes (vista previa del editor). Devuelve '' cuando no hay política.
 */
export interface CancellationPolicyOpts {
  /** Ventana sin cargo, en horas (tambien la ventana efectiva de cambios #578). */
  windowHours: number;
  /** % que se retiene al cancelar DENTRO de la ventana (cancelación tardía). */
  latePct: number;
  /** % que se retiene por inasistencia (no-show). */
  noShowPct: number;
  /** Precio del servicio (pesos). Si falta o <= 0, el texto va sin montos. */
  price?: number | null;
  /** #578 (D3) — tope de cambios de horario por reserva. null/undefined = ilimitado. */
  rescheduleLimit?: number | null;
}

function money(price: number, pct: number): string {
  return `$${((price * pct) / 100).toLocaleString('es-MX', { minimumFractionDigits: 2 })}`;
}

export function cancellationPolicyText(opts: CancellationPolicyOpts): string {
  const { windowHours, latePct, noShowPct } = opts;
  const hasCancelPolicy = latePct > 0 || noShowPct > 0;
  const price = opts.price != null && opts.price > 0 ? opts.price : null;
  const amt = (pct: number) => (price != null ? ` (${money(price, pct)})` : '');

  const parts: string[] = [];
  if (hasCancelPolicy) {
    parts.push(`Cancela con más de ${windowHours} h de anticipación y te reembolsamos el 100%.`);
    if (latePct > 0) {
      parts.push(
        `Si cancelas con menos de ${windowHours} h, se retiene el ${latePct}%${amt(latePct)} de tu pago` +
          ` y se te reembolsa el resto${amt(100 - latePct)}.`,
      );
    }
    if (noShowPct > 0) {
      parts.push(
        noShowPct >= 100
          ? `Si no asistes, se retiene el 100%${amt(100)} de tu pago.`
          : `Si no asistes, se retiene el ${noShowPct}%${amt(noShowPct)} de tu pago y se te reembolsa el resto${amt(100 - noShowPct)}.`,
      );
    }
  }

  // #578 (D3) — tope de cambios de horario + anticipacion, en la politica aceptada.
  // Con limite: "hasta N veces y solo con mas de H h..."; sin limite: solo la anticipacion.
  if (windowHours > 0 || opts.rescheduleLimit != null) {
    const anticip = windowHours > 0 ? ` y solo con más de ${windowHours} h de anticipación` : '';
    if (opts.rescheduleLimit != null) {
      parts.push(
        `Puedes cambiar tu horario hasta ${opts.rescheduleLimit} ` +
          `${opts.rescheduleLimit === 1 ? 'vez' : 'veces'}${anticip}; después solo podrás cancelar o asistir.`,
      );
    } else if (windowHours > 0) {
      parts.push(`Puedes cambiar tu horario solo con más de ${windowHours} h de anticipación.`);
    }
  }
  return parts.join(' ');
}
